import type { ProgressMap } from './progress';

export interface ApiUser {
  readonly id: string;
  readonly isGuest: boolean;
  readonly email: string | null;
  readonly displayName: string | null;
  readonly createdAt: string;
}

export interface ApiSession {
  readonly token: string;
  readonly expiresAt: string;
}

export interface ApiProgressEntry {
  readonly levelId: string;
  readonly stars: number;
  readonly bestScore: number;
  readonly completedAt: string;
  readonly attempts: number;
}

/** Everything the account remembers about play: level results and story beats seen. */
export interface ApiProgressSnapshot {
  readonly progress: ApiProgressEntry[];
  readonly cutscenes: string[];
}

export interface ApiAchievement {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly category: string;
  readonly hidden: boolean;
  readonly unlockedAt: string | null;
}

export interface ApiRunResult {
  readonly run: { readonly levelId: string; readonly score: number; readonly stars: number };
  /** Null for endless runs, which are scored but never enter the progress map. */
  readonly progress: ApiProgressEntry | null;
  readonly unlockedAchievements?: readonly string[];
}

export interface RunSubmission {
  readonly levelId: string;
  readonly selectedEdgeIds: readonly string[];
  readonly edgeFlows?: Readonly<Record<string, number>>;
  readonly durationMs?: number;
  readonly hintsUsed?: number;
  readonly undoCount?: number;
  readonly edgesSelectedDuringSession?: number;
}

export type ApiFailureReason = 'offline' | 'unauthorized' | 'rejected';

export interface ApiFailure {
  readonly ok: false;
  readonly reason: ApiFailureReason;
  readonly message: string;
  readonly status?: number;
}

export type ApiResult<T> = { readonly ok: true; readonly value: T } | ApiFailure;

const TOKEN_KEY = 'graphquest.auth.v1';
const REQUEST_TIMEOUT_MS = 8000;
const OFFLINE_COOLDOWN_MS = 30_000;

const BASE_URL = readBaseUrl();

let offlineUntil = 0;

function readBaseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE_URL as string | undefined;
  const raw = configured && configured.trim().length > 0 ? configured.trim() : '/api/v1';
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {}
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

export function isSignedIn(): boolean {
  return getToken() !== null;
}

export function isBackendUnreachable(): boolean {
  return Date.now() < offlineUntil;
}

function offline(message: string): ApiFailure {
  offlineUntil = Date.now() + OFFLINE_COOLDOWN_MS;
  return { ok: false, reason: 'offline', message };
}

function failureFor(status: number, message: string): ApiFailure {
  if (status === 401) return { ok: false, reason: 'unauthorized', message, status };
  if (status >= 500) return offline(message);
  return { ok: false, reason: 'rejected', message, status };
}

async function messageOf(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: { message?: string } };
    return body.error?.message ?? `Request failed (HTTP ${response.status}).`;
  } catch {
    return `Request failed (HTTP ${response.status}).`;
  }
}

interface RequestOptions {
  readonly method: 'GET' | 'POST' | 'PATCH';
  readonly path: string;
  readonly body?: unknown;
  readonly authenticated: boolean;
}

async function request<T>(options: RequestOptions): Promise<ApiResult<T>> {
  if (isBackendUnreachable()) {
    return { ok: false, reason: 'offline', message: 'The server is currently unreachable.' };
  }

  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  if (options.authenticated) {
    const token = getToken();
    if (!token) {
      return { ok: false, reason: 'unauthorized', message: 'No session token stored.' };
    }
    headers.authorization = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => {
    controller.abort();
  }, REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${options.path}`, {
      method: options.method,
      headers,
      signal: controller.signal,
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch {
    return offline('The server could not be reached.');
  } finally {
    clearTimeout(timeout);
  }

  offlineUntil = 0;

  if (response.status === 401) clearToken();
  if (!response.ok) return failureFor(response.status, await messageOf(response));
  if (response.status === 204) return { ok: true, value: undefined as T };

  try {
    return { ok: true, value: (await response.json()) as T };
  } catch {
    return { ok: false, reason: 'rejected', message: 'The server sent an unreadable response.' };
  }
}

interface AuthResponse {
  readonly user: ApiUser;
  readonly session: ApiSession;
}

async function startSession(path: string, body: unknown): Promise<ApiResult<ApiUser>> {
  const result = await request<AuthResponse>({
    method: 'POST',
    path,
    body,
    authenticated: false,
  });
  if (!result.ok) return result;

  setToken(result.value.session.token);
  return { ok: true, value: result.value.user };
}

export function createGuestAccount(displayName?: string): Promise<ApiResult<ApiUser>> {
  return startSession('/auth/guest', displayName === undefined ? {} : { displayName });
}

export function login(email: string, password: string): Promise<ApiResult<ApiUser>> {
  return startSession('/auth/login', { email, password });
}

export async function upgradeAccount(email: string, password: string): Promise<ApiResult<ApiUser>> {
  const result = await request<{ user: ApiUser }>({
    method: 'POST',
    path: '/auth/upgrade',
    body: { email, password },
    authenticated: true,
  });
  return result.ok ? { ok: true, value: result.value.user } : result;
}

export async function logout(): Promise<void> {
  await request<void>({ method: 'POST', path: '/auth/logout', authenticated: true });
  clearToken();
}

export async function fetchCurrentUser(): Promise<ApiResult<ApiUser>> {
  const result = await request<{ user: ApiUser }>({
    method: 'GET',
    path: '/me',
    authenticated: true,
  });
  return result.ok ? { ok: true, value: result.value.user } : result;
}

export async function updateDisplayName(displayName: string): Promise<ApiResult<ApiUser>> {
  const result = await request<{ user: ApiUser }>({
    method: 'PATCH',
    path: '/me',
    body: { displayName },
    authenticated: true,
  });
  return result.ok ? { ok: true, value: result.value.user } : result;
}

export async function ensureSession(guestDisplayName?: string): Promise<ApiResult<ApiUser>> {
  if (isSignedIn()) {
    const existing = await fetchCurrentUser();
    if (existing.ok || existing.reason !== 'unauthorized') return existing;
  }
  return createGuestAccount(guestDisplayName);
}

export function submitRun(submission: RunSubmission): Promise<ApiResult<ApiRunResult>> {
  return request<ApiRunResult>({
    method: 'POST',
    path: '/runs',
    body: submission,
    authenticated: true,
  });
}

export function fetchProgress(): Promise<ApiResult<ApiProgressSnapshot>> {
  return request<ApiProgressSnapshot>({ method: 'GET', path: '/progress', authenticated: true });
}

export function mergeLocalProgress(
  progress: ProgressMap,
  cutscenes: readonly string[],
): Promise<ApiResult<ApiProgressSnapshot>> {
  if (Object.keys(progress).length === 0 && cutscenes.length === 0) return fetchProgress();

  return request<ApiProgressSnapshot>({
    method: 'POST',
    path: '/progress/merge',
    body: { progress, cutscenes },
    authenticated: true,
  });
}

export async function fetchAchievements(): Promise<ApiResult<ApiAchievement[]>> {
  const result = await request<{ achievements: ApiAchievement[] }>({
    method: 'GET',
    path: '/achievements',
    authenticated: true,
  });
  return result.ok ? { ok: true, value: result.value.achievements } : result;
}

export function toProgressMap(entries: readonly ApiProgressEntry[]): ProgressMap {
  const map: Record<string, { stars: number; bestScore: number; completedAt: string }> = {};
  for (const entry of entries) {
    map[entry.levelId] = {
      stars: entry.stars,
      bestScore: entry.bestScore,
      completedAt: entry.completedAt,
    };
  }
  return map;
}
